import { useMemo, useState, type CSSProperties } from 'react';
import type { CharacterSlotId, ModuleRendererProps } from '@momentum/shared';
import type { ModuleRenderer } from '../renderer.ts';
import { CharacterBadge } from '../board/CharacterBadge.tsx';
import { useDeadline } from '../board/useDeadline.ts';
import { PocChoiceView } from './manifest.ts';

/** Visually-hidden but screen-reader-visible text (no CSS file to define a `.sr-only` class here). */
const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function phaseLabel(phase: PocChoiceView['phase']): string {
  switch (phase) {
    case 'think':
      return 'Thinking privately';
    case 'discuss':
      return 'Discussing';
    case 'resolved':
      return 'Resolved';
    case 'complete':
      return 'Complete';
  }
}

/** Decorative black rounded-square "receiver" placeholder (see docs/ASSETS.md — the customer robot scene actor). */
function ReceiverGlyph() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg width={28} height={28} viewBox="0 0 40 40" aria-hidden="true">
        <rect x={4} y={4} width={32} height={32} rx={8} fill="var(--char-charcoal, #2b2d33)" />
        <circle cx={15} cy={19} r={3.2} fill="#fff" />
        <circle cx={25} cy={19} r={3.2} fill="#fff" />
      </svg>
      <span style={srOnly}>Customer</span>
    </span>
  );
}

function seconds(remainingMs: number | null): number {
  return remainingMs === null ? 0 : Math.ceil(remainingMs / 1000);
}

function PocChoiceRenderer({ instance, session, you, send, serverTimeOffset, readOnly }: ModuleRendererProps) {
  const view = useMemo(() => PocChoiceView.parse(instance.view), [instance.view]);
  const youId = you?.participantId ?? null;
  const isDecider = youId !== null && youId === view.deciderId;

  const think = useDeadline(instance.deadlines, 'think', serverTimeOffset);
  const discuss = useDeadline(instance.deadlines, 'discuss', serverTimeOffset);
  const resolved = useDeadline(instance.deadlines, 'resolved', serverTimeOffset);

  const participantsById = useMemo(() => new Map(session.participants.map((p) => [p.id, p])), [session.participants]);
  const nameOf = (id: string): string => (id === youId ? 'You' : (participantsById.get(id)?.displayName ?? id));
  const slotOf = (id: string): CharacterSlotId | null => participantsById.get(id)?.characterSlot ?? null;
  const optionLabel = (optionId: string | null): string =>
    optionId ? (view.options.find((o) => o.id === optionId)?.label ?? optionId) : 'nothing';

  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [rejects, setRejects] = useState<{ suggest: string; commit: string }>({ suggest: '', commit: '' });

  async function suggest(optionId: string): Promise<void> {
    setPendingKey(`suggest:${optionId}`);
    const outcome = await send('suggest', { optionId });
    setPendingKey(null);
    setRejects((prev) => ({ ...prev, suggest: outcome.ok ? '' : outcome.message || outcome.code }));
  }

  async function commit(optionId: string): Promise<void> {
    setPendingKey(`commit:${optionId}`);
    const outcome = await send('commit', { optionId });
    setPendingKey(null);
    setRejects((prev) => ({ ...prev, commit: outcome.ok ? '' : outcome.message || outcome.code }));
  }

  const deciderName = nameOf(view.deciderId);
  const currentStepCommitted = view.committed.find((c) => c.step === view.step) ?? null;
  const suggestersByOption = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [participantId, optionId] of Object.entries(view.suggestions)) {
      const list = map.get(optionId) ?? [];
      list.push(participantId);
      map.set(optionId, list);
    }
    return map;
  }, [view.suggestions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ margin: 0, color: 'var(--ink-muted, #5b6270)' }}>
          Step {view.step} of {view.steps} · {phaseLabel(view.phase)}
        </p>
        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReceiverGlyph />
          {view.prompt}
        </p>
        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CharacterBadge slot={slotOf(view.deciderId)} size={28} title={deciderName} emphasis="turn" />
          <strong>{isDecider ? 'You decide.' : `${deciderName} decides.`}</strong>
          {isDecider && <span style={{ color: 'var(--accent, #1a73ff)' }}>Your turn</span>}
        </p>
      </header>

      {view.phase === 'think' && (
        <p role="status" style={{ margin: 0 }}>
          Private picks · {seconds(think.remainingMs)}s{think.paused ? ' (paused)' : ''} · {view.suggestedCount} picked so far
        </p>
      )}
      {view.phase === 'discuss' && (
        <p role="status" style={{ margin: 0 }}>
          Discussing · {seconds(discuss.remainingMs)}s left{discuss.paused ? ' (paused)' : ''}
        </p>
      )}
      {view.phase === 'resolved' && currentStepCommitted && (
        <div role="status" style={{ margin: 0 }}>
          <p style={{ margin: 0 }}>
            <strong>{nameOf(currentStepCommitted.byParticipantId)}</strong> committed to {optionLabel(currentStepCommitted.optionId)}
            {currentStepCommitted.timedOut ? ' (timed out)' : ''}. Next step in {seconds(resolved.remainingMs)}s.
          </p>
          {view.facts.length > 0 && <p style={{ margin: 0 }}>New fact: {view.facts[view.facts.length - 1]}</p>}
        </div>
      )}

      <div role="group" aria-label="Options" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        {view.options.map((option) => {
          const isMine = view.mySuggestion === option.id;
          const suggesters = suggestersByOption.get(option.id) ?? [];
          const isCommittedHere = currentStepCommitted?.optionId === option.id;
          const canSuggestNow = !readOnly && (view.phase === 'think' || view.phase === 'discuss');
          const canCommitNow = !readOnly && view.canCommit && view.phase === 'discuss';
          return (
            <div
              key={option.id}
              style={{
                border: `1px solid ${isCommittedHere ? 'var(--highlight, #ffcc00)' : 'var(--line, #e2e5ea)'}`,
                borderRadius: 'var(--radius-card, 16px)',
                padding: 12,
                background: isMine ? 'var(--accent-soft, #e8f0ff)' : 'var(--surface, #f6f7f9)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <p style={{ margin: 0, fontWeight: 600 }}>{option.label}</p>

              {view.phase === 'think' && isMine && <p style={{ margin: 0 }}>Your pick</p>}

              {view.phase !== 'think' && suggesters.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} aria-label={`Suggested by ${suggesters.map(nameOf).join(', ')}`}>
                  {suggesters.map((pid) => (
                    <CharacterBadge key={pid} slot={slotOf(pid)} size={24} title={nameOf(pid)} emphasis={pid === youId ? 'you' : 'none'} />
                  ))}
                </div>
              )}

              {isCommittedHere && <p style={{ margin: 0, fontWeight: 600 }}>Committed{currentStepCommitted?.timedOut ? ' (timed out)' : ''}</p>}

              {canSuggestNow && (
                <button
                  type="button"
                  onClick={() => void suggest(option.id)}
                  disabled={pendingKey === `suggest:${option.id}`}
                  aria-pressed={isMine}
                  style={{ minHeight: 'var(--touch-target, 44px)', borderRadius: 'var(--radius-control, 12px)' }}
                >
                  {isMine ? 'Picked' : 'Pick this'}
                </button>
              )}

              {canCommitNow && (
                <button
                  type="button"
                  onClick={() => void commit(option.id)}
                  disabled={pendingKey === `commit:${option.id}`}
                  style={{
                    minHeight: 'var(--touch-target, 44px)',
                    borderRadius: 'var(--radius-control, 12px)',
                    background: 'var(--highlight, #ffcc00)',
                  }}
                >
                  Commit to this
                </button>
              )}
            </div>
          );
        })}
      </div>

      {rejects.suggest && (
        <p role="alert" style={{ color: 'var(--danger, #d64545)', margin: 0 }}>
          {rejects.suggest}
        </p>
      )}
      {rejects.commit && (
        <p role="alert" style={{ color: 'var(--danger, #d64545)', margin: 0 }}>
          {rejects.commit}
        </p>
      )}

      {view.phase === 'discuss' && !view.canCommit && (
        <p style={{ margin: 0 }}>
          Everyone talks. {deciderName} decides.
        </p>
      )}

      {view.phase === 'complete' && (
        <div>
          <h3 style={{ margin: '0 0 4px' }}>Outcome</h3>
          <p style={{ margin: '0 0 8px' }}>Outcome key: {view.outcome ?? 'none'}</p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {view.facts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </div>
      )}
      {view.phase !== 'complete' && view.phase !== 'resolved' && view.facts.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 4px' }}>So far</h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {view.facts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const pocChoiceClientRenderer: ModuleRenderer = {
  renderer: 'poc-choice',
  Component: PocChoiceRenderer,
};
